import React, { Fragment, useState } from 'react';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Icon, Table } from 'rsuite';
import styled from 'styled-components';
import { UserType } from '../../../../../interfaces/portal';
import reservationAddressLine from '../../../../../helpers/reservationAddressLine';
import guestAddressLine from '../../../../../helpers/guestAddressLine';
import isPortalGuestAddressSynced from '../../../../../helpers/isPortalGuestAddressSynced';
import { parseInt } from '../../../../../helpers/utils';
import ConfirmationModal from '../../../../ConfirmationModal';
import { syncPortalUserGuestAddress } from '../../../../../redux/Settings/sagas/portal/routines';
import AddressLines from './AddressLines';

const AddressLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
`;

const AddressValue = styled.div`
  margin-bottom: 6px;
  line-height: 1.3;
`;

const SyncButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 12px;
`;

const isHomeless = value => parseInt(value) === 1;

const formatPortalAddress = otherInfo => {
  if (isHomeless(get('homeless', otherInfo))) {
    return { line1: 'Homeless', line2: '' };
  }

  const { line1, line2 } = reservationAddressLine(otherInfo, true);
  if (!line1 && !line2) {
    return { line1: '—', line2: '' };
  }

  return { line1: line1 || '—', line2: line2 || '' };
};

const formatGuestAddress = guest => {
  if (!guest) {
    return { line1: '—', line2: '' };
  }

  if (isHomeless(guest.homeless)) {
    return { line1: 'Homeless', line2: '' };
  }

  const { line1, line2 } = guestAddressLine(
    {
      street_address: guest.streetAddress,
      apartment: guest.apartment,
      city: guest.city,
      county: guest.county,
      state: guest.state,
      zipcode: guest.zipCode
    },
    true,
    false
  );
  if (!line1 && !line2) {
    return { line1: '—', line2: '' };
  }

  return { line1, line2 };
};

const AddressCell = ({ rowData, ...props }) => {
  const dispatch = useDispatch();
  const [showConfirm, setShowConfirm] = useState(false);
  const otherInfo = get('otherInfo', rowData) || {};
  const portalAddress = formatPortalAddress(otherInfo);
  const linkedGuest = get('linkedGuest', rowData);
  const guestAddress = formatGuestAddress(linkedGuest);
  const isAddressSynced = isPortalGuestAddressSynced(otherInfo, linkedGuest);
  const showSyncDetails = linkedGuest && !isAddressSynced;

  const handleConfirm = () => {
    if (!rowData || !rowData.id) return;
    setShowConfirm(false);
    dispatch(syncPortalUserGuestAddress(rowData.id));
  };

  return (
    <Table.Cell style={{ marginTop: -8 }} {...props}>
      {showSyncDetails ? (
        <Fragment>
          <AddressLabel>Portal Address</AddressLabel>
          <AddressValue>
            <AddressLines
              line1={portalAddress.line1}
              line2={portalAddress.line2}
            />
          </AddressValue>
          <AddressLabel>Internal Guest Address</AddressLabel>
          <AddressValue style={{ marginBottom: 4 }}>
            <AddressLines
              line1={guestAddress.line1}
              line2={guestAddress.line2}
            />
          </AddressValue>
          <SyncButton
            appearance="link"
            onClick={e => {
              e.stopPropagation();
              setShowConfirm(true);
            }}
          >
            <Icon icon="refresh" /> Sync Address
          </SyncButton>
          <ConfirmationModal
            show={showConfirm}
            title="Sync Address"
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Sync Address"
            onCancel={() => setShowConfirm(false)}
            onConfirm={handleConfirm}
          >
            This will overwrite the internal guest address with the Portal
            address. Do you want to continue?
          </ConfirmationModal>
        </Fragment>
      ) : (
        <AddressLines line1={portalAddress.line1} line2={portalAddress.line2} />
      )}
    </Table.Cell>
  );
};

AddressCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: UserType
};

AddressCell.defaultProps = {
  rowData: null
};

export default AddressCell;
